import { ReactElement } from 'react'
import { useSnackbar } from '@ui/snackbar'
import Results2ActionBar, { type Results2ActionBarStatus } from '../../Results2ActionBar'
import { UNSAVED_BEMESSUNG_NAV_SNACKBAR_KEY } from '../../Results2DetailPane/constants'

interface Props {
  isSaving: boolean
  /** Manual loads edited but not yet saved — saving them recomputes the anchor checks. */
  manualLoadsDirty: boolean
  /** Comment edited but not yet saved. */
  commentDirty: boolean
  /** Zuganker changed but not yet saved — saving it recomputes the anchor checks. */
  connectorSelectionDirty: boolean
  /** An incomplete or unparsable manual-load row — blocks saving until it is fixed. */
  hasInvalidDraft: boolean
  disabled: boolean
  /** Blocks saving without counting as disabled for "Rückgängig" (e.g. the corner helper runs). */
  saveBlocked: boolean
  onSave: () => void
  /** Drops all local edits (manual loads, comment and Zuganker). */
  onRevert: () => void
}

/**
 * Footer of the anchor detail pane. Edited manual loads and comment are only saved here (manual
 * loads also recompute the anchor checks and reload the results) or dropped via "Rückgängig";
 * until then the check table previews the manual loads.
 */
const AnchorSaveRow = ({
  isSaving,
  manualLoadsDirty,
  commentDirty,
  connectorSelectionDirty,
  hasInvalidDraft,
  disabled,
  saveBlocked,
  onSave,
  onRevert,
}: Props): ReactElement => {
  const { closeSnackbar } = useSnackbar()
  const isDirty = manualLoadsDirty || commentDirty || connectorSelectionDirty

  const status: Results2ActionBarStatus = isSaving
    ? 'saving'
    : hasInvalidDraft
    ? 'invalid'
    : isDirty
    ? 'dirty'
    : 'saved'
  const statusLabel = isSaving
    ? 'Wird gespeichert…'
    : hasInvalidDraft
    ? 'Unvollständige Zeile – bitte ergänzen'
    : isDirty
    ? 'Ungespeicherte Änderungen'
    : 'Gespeichert'

  return (
    <Results2ActionBar
      status={status}
      statusLabel={statusLabel}
      saveDisabled={!isDirty || hasInvalidDraft || saveBlocked || disabled}
      revertDisabled={!isDirty || isSaving || disabled}
      onSave={() => {
        onSave()
        closeSnackbar(UNSAVED_BEMESSUNG_NAV_SNACKBAR_KEY)
      }}
      onRevert={() => {
        onRevert()
        closeSnackbar(UNSAVED_BEMESSUNG_NAV_SNACKBAR_KEY)
      }}
      saveDataCy="anchor-detail-save-btn"
      revertDataCy="anchor-detail-revert-btn"
    />
  )
}

export default AnchorSaveRow
